import { datadogRum } from '@datadog/browser-rum';
import { Box, styled, Tabs, Typography } from '@mui/material';
import { PageHeaderTab } from '@side/pantry';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { usePrevious } from 'react-use';
import { ErrorPage } from 'src/components/ErrorPage';
import { ErrorPageType } from 'src/components/ErrorPage/ErrorPage.constants';
import { PageTitle } from '@/components/PageTitle/PageTitle';
import { useReports } from '@/components/Reports/useReports/useReports';
import { Spinner } from '@/components/Spinner';
import { trackReport } from '@/modules/user-tracking/helpers';
import { contentContainerSx } from '@/theme/contentContainerSx';
import { CompensationReport } from '@/views/Reports/CompensationReport/CompensationReport';
import { ReportProvider } from '@/views/Reports/context/ReportProvider';
import { useViewReportAs } from '@/views/Reports/helpers';
import { PerformanceReportProvider } from '@/views/Reports/PerformanceReport/context/PeformanceReportProvider';
import { PerformanceReport } from '@/views/Reports/PerformanceReport/PerformanceReport';
import { PlatformFeeReport } from '@/views/Reports/PlatformFeeReport/PlatformFeeReport';
import { useAppFlags } from '$launch-darkly/app-flags';

const StyledIframe = styled('iframe')`
  width: 100%;
  height: 100%;
  border: none;
  &:not([src*='periscopedata.com']) {
    /* Match Periscope's top border for non-Periscope reports */
    border-top: 1px solid #ced1d7;
  }
`;

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ReportContentWrapper = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const ReportsDashboard = () => {
  const { query, replace } = useRouter();

  const { report: reportId } = query;
  const viewReportAs = useViewReportAs();
  const parsedId = parseInt(`${reportId}`, 10);

  // Captured here (outside the `key={viewReportAs}` subtree) so filter
  // providers can distinguish a view-as switch from an initial mount and clear
  // the previous user's agent/team selections accordingly.
  const previousViewReportAs = usePrevious(viewReportAs);

  // just in-case somebody lands directly on reports page via a shared link, we
  // would want to refetch the data.
  const { reports, loading } = useReports();
  const { paymentOwnsReportsV2 } = useAppFlags();

  const filteredReport = useMemo(
    () => reports.find((report) => report.id === parsedId),
    [parsedId, reports],
  );

  const getReportComponent = useCallback(
    (reportName: string | undefined) => {
      const iframe = <StyledIframe title="reports" src={filteredReport?.url} />;

      switch (reportName) {
        case 'Compensation':
          // key forces a remount when the viewed user changes — same reason as
          // PerformanceReportProvider below.
          return paymentOwnsReportsV2 ? (
            <CompensationReport
              key={viewReportAs}
              previousViewReportAs={previousViewReportAs}
            />
          ) : (
            iframe
          );
        case 'Performance':
          return paymentOwnsReportsV2 ? (
            // key forces a remount when the viewed user changes, ensuring
            // useLocalStorageState inside the provider re-initializes from the
            // correct per-user key rather than carrying over mid-mount state.
            <PerformanceReportProvider
              key={viewReportAs}
              previousViewReportAs={previousViewReportAs}
            >
              <PerformanceReport />
            </PerformanceReportProvider>
          ) : (
            iframe
          );
        case 'Platform Fee':
          return <PlatformFeeReport key={viewReportAs} />;
        default:
          return iframe;
      }
    },
    [
      filteredReport?.url,
      paymentOwnsReportsV2,
      viewReportAs,
      previousViewReportAs,
    ],
  );

  useEffect(() => {
    if (!filteredReport) return;

    const track = async () => {
      try {
        await trackReport({
          url: window.location.pathname + window.location.search,
          reportName: filteredReport.name,
        });
      } catch (error) {
        datadogRum.addError(error, {
          message: 'Failed to update last viewed location',
        });
      }
    };

    void track();
  }, [filteredReport]);

  // If the user lands on this page without a selected report, default to the
  // first report
  useEffect(() => {
    if (!reportId && reports.length) {
      const viewAsQuery = viewReportAs ? `&viewReportAs=${viewReportAs}` : '';
      void replace(`reports?report=${reports[0].id}${viewAsQuery}`);
    }
  }, [reportId, loading, reports, replace, viewReportAs]);

  const selectedReport = reports.find((report) => report.id === parsedId);

  if (loading) {
    return <Spinner fullScreen />;
  }

  return (
    <ReportProvider>
      <Container>
        <PageTitle title={`${selectedReport?.name ?? 'Reports'} | Side`} />

        <Typography
          variant="h1"
          sx={{
            ...contentContainerSx,
            color: 'other.spaceCadet',
            py: 2,
          }}
        >
          Reports
        </Typography>

        <Tabs
          value={filteredReport?.id ?? false}
          variant="scrollable"
          scrollButtons={false}
        >
          {reports.map(({ name, id }) => (
            <PageHeaderTab
              key={id}
              label={name}
              value={id}
              LinkComponent={Link}
              href={`/reports?report=${id}${viewReportAs ? `&viewReportAs=${viewReportAs}` : ''}`}
              sx={contentContainerSx}
            />
          ))}
        </Tabs>

        <ReportContentWrapper>
          {reports.length ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {getReportComponent(filteredReport?.name)}
            </Box>
          ) : (
            <ErrorPage errorType={ErrorPageType.NoReportsFound} />
          )}
        </ReportContentWrapper>
      </Container>
    </ReportProvider>
  );
};
